import React, { FC, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Radio,
  Card,
  Button,
  InputNumber,
  Typography,
  Row,
  Col,
  Space,
  Upload,
  List,
  Divider,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { IDocumentFields } from "../../../../interfaces/ICase";
import DocumentUpload from "./DocumentUpload";
import ResponseDocumentUpload from "./ResponseDocumentUpload";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { useUserFunctionalities } from "../../../../utils/UserFunctionalities";

const { Title } = Typography;

interface FormValues {
  starhealthHospitalizationReason: {
    id: number;
    name: string;
  };
  procedureCorporateMapping: {
    id: number;
    name: string;
  };
  treatmentType: number;
  starhealthProcedureDiagnosis: {
    id: number;
    name: string;
  };
  dateOfDiagnosis: dayjs.Dayjs;
  chronicIllness: {
    id: number;
    name: string;
  };
  chronicIllnessDate?: dayjs.Dayjs;
  noOfMonths?: number;
  doctorName: string;
  doctorQualification: string;
  department?: {
    id: number;
    name: string;
  };
  otherProcedureText?: string;
  otherProcedureDiagnosisText?: string;
  casePreAuthStarhealthBillDetailsRequestList: Array<{
    irdaiBillCategoryId: number;
    starHealthBillSubCategoryId: number;
    numberOfUnits: number;
    estimateAmount: number;
  }>;
}

interface PreAuthStarHealthProps {
  form: any;
  onFinish: (values: FormValues) => Promise<void>;
  onSaveDraft: (values: FormValues) => Promise<void>;
  onIntimateSubmit: (values: FormValues) => Promise<void>;
  onAddCategory: () => void;
  onDeleteCategory: (index: number) => Promise<void>;
  isLoading: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  isIntimating: boolean;
  totalAmount: number;
  reasonList: any[];
  diagnosisList: any[];
  chronicIllnessList: any[];
  procedureList: any[];
  specialityList: any[];
  qualificationList: any[];
  billCategories: any[];
  billSubCategories: any[];
  onBillCategoryChange: (categoryId: number) => Promise<void>;
  handleFileChange:  any;
  documentTypes: any[];
  documentUrls: {
    documentType: string;
    documentName: string;
    documentUrl: string;
    id: number;
    referenceType: string | null;
    documentLabelType: any;
    deficiencyCheckStatus: any;
    remarks: string | null;
  }[];
  handleSubmit: () => void;
  documentRequestResponseDocuments: any;
  handleSaveConfirmationModal: (state: string) => void;
  loading: boolean;
  portalData?: any;
  handleOpen: any;
  handleSaveData: () => Promise<void>;
  isApproved: boolean;
  isApprovedInitiated: boolean;
  handleApprove: () => Promise<void>;
  preAuth: any;
  activeKey: any;
  disabled: any;
  showDocuments: boolean;
  handleGetDocumentsStarHealth: any;
  renderUploadButton: any;
  uploadFileList: any;
  isSubmitted?: boolean;
  showVerifyButton?: boolean;
  isValidationLoading?: boolean;
  registrationValidationStatus?: any;
  handleClaimValidation?: () => void;
}

const PreAuthStarHealth: FC<PreAuthStarHealthProps> = ({
  form,
  onFinish,
  onSaveDraft,
  onIntimateSubmit,
  onAddCategory,
  onDeleteCategory,
  isLoading,
  isSaving,
  isSubmitting,
  isIntimating,
  totalAmount,
  reasonList,
  diagnosisList,
  chronicIllnessList,
  procedureList,
  specialityList,
  qualificationList,
  billCategories,
  billSubCategories,
  onBillCategoryChange,
  handleFileChange,
  documentTypes,
  documentUrls,
  handleSubmit,
  documentRequestResponseDocuments,
  handleSaveConfirmationModal,
  loading,
  portalData,
  handleOpen,
  isApproved,
  isApprovedInitiated,
  handleApprove,
  preAuth,
  activeKey,
  handleSaveData,
  disabled,
  showDocuments,
  handleGetDocumentsStarHealth,
  renderUploadButton,
  uploadFileList,
  isSubmitted,
  showVerifyButton,
  isValidationLoading,
  registrationValidationStatus,
  handleClaimValidation,
}) => {
  const { Option } = Select;
  const { auth, patients } = useSelector((state: RootState) => state);
  const { hasFunctionality } = useUserFunctionalities();

  // Watch procedure selection to show/hide conditional fields
  const selectedProcedure = Form.useWatch(
    ["procedureCorporateMapping", "id"],
    form
  );
  const selectedChronicIllness = Form.useWatch(["chronicIllness", "id"], form);
  const selectedTreatmentType = Form.useWatch("treatmentType", form);

  // Filter speciality list based on treatment type
  const filteredSpecialityList = specialityList?.filter(
    (spec) => spec.lineOfTreatmentId === selectedTreatmentType
  );

  // Remove diagnosis list filtering
  // const filteredDiagnosisList = diagnosisList.filter(
  //   (diagnosis) => diagnosis.procedureCorporateMappingId === selectedProcedure
  // );

  const isProcedureOthers =
    procedureList
      ?.find((p) => p.id === selectedProcedure)
      ?.procedureMethodName?.toUpperCase() === "OTHERS";
  const isChronicIllnessOthers =
    chronicIllnessList
      ?.find((c) => c.id === selectedChronicIllness)
      ?.illnessName?.toUpperCase() === "OTHERS";

  const hasClaimId = Boolean(portalData?.casePreAuthStarhealth?.claimId);

   useEffect(() => {
    const billDetails = form.getFieldValue("casePreAuthStarhealthBillDetailsRequestList");
    if (!billDetails || billDetails?.length === 0) {
      form.setFieldsValue({
        casePreAuthStarhealthBillDetailsRequestList: [{}],
      });
    }
  }, [form]);

  return (
    <div style={{ margin: "0px 0.5rem" }}>
      <Form
        form={form}
        onFinish={handleSubmit}
        labelAlign="left"
        layout="vertical"
        labelWrap
        style={{ width: "100%" }}
      >
        <Title level={4}>PreAuth Details</Title>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label="CLAIM TYPE"
              name="claimType"
              rules={[{ required: true, message: "Please select claim type" }]}
            >
              <Radio.Group>
                <Radio value="COVID">COVID</Radio>
                <Radio value="NON-COVID">NON-COVID</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="POLICY TYPE"
              name="policyType"
              rules={[{ required: true, message: "Please select policy type" }]}
            >
              <Radio.Group>
                <Radio value="INDIVIDUAL">INDIVIDUAL</Radio>
                <Radio value="GROUP">GROUP</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label="MEDICAL ID"
              name="beneficiaryId"
              rules={[{ required: true, message: "Please enter medical ID" }]}
            >
              <Input
                disabled={portalData?.schemeRegistrationId}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="REGISTRATION DATE"
              name="registrationDate"
              rules={[
                { required: true, message: "Please select registration date" },
              ]}
            >
              <DatePicker
                format="DD-MM-YYYY HH:mm:ss"
                showTime
                style={{ width: "100%" }}
                disabled={portalData?.schemeRegistrationId}
              />
            </Form.Item>
          </Col>

          {
            Form.useWatch("policyType", form) === "GROUP" ? <Col span={8}>
            <Form.Item
              label="Employee ID"
              name="employeeId"
              // rules={[
              //   { required: true, message: "Please enter Employee id" },
              // ]}
            >
              <Input
                // disabled={true}
              />
            </Form.Item>
          </Col> : <></>
          }

          <Col span={8}>
            <Form.Item
              label="HOSPITAL PATIENT ID"
              name="patientOpdIpdNumber"
              rules={[
                { required: true, message: "Please enter hospital patient ID" },
              ]}
            >
              <Input
                // disabled={true}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="MEDICAL ID CARD NUMBER"
              name="beneficiaryId"
              rules={[
                {
                  required: true,
                  message: "Please enter medical ID card number",
                },
              ]}
            >
              <Input
                disabled={portalData?.schemeRegistrationId}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="POLICY NAME" name="policyName">
              <Input
                disabled={true}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="CLAIM REF NO." name="claimRefNo">
              <Input
                disabled={true}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="POLICY NO." name="policyNumber">
              <Input
               disabled={true}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="POLICY INCEPTION DATE" name="policyDate">
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: "100%" }}
                disabled={true}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="POLICY START DATE" name="policyStartDate">
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: "100%" }}
                disabled={true}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="POLICY END DATE" name="policyEndDate">
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: "100%" }}
                disabled={true}
              />
            </Form.Item>
          </Col>
        </Row>


        <Title level={4}>Illness Details</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={["starhealthHospitalizationReason", "id"]}
              label="Reason for Hospitalization"
              rules={[
                {
                  required: true,
                  message: "Please select reason for hospitalization",
                },
              ]}
            >
              <Select
                placeholder="Select reason"
                disabled={disabled}
                onChange={(value) =>
                  form.setFieldsValue({
                    starhealthHospitalizationReason: {
                      id: value,
                      name: reasonList?.find(
                        (r) => r.starhealthHospitalizationReasonId === value
                      )?.eventName,
                    },
                  })
                }
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {reasonList?.map((reason) => (
                  <Option
                    key={reason.starhealthHospitalizationReasonId}
                    value={reason.starhealthHospitalizationReasonId}
                  >
                    {reason.eventName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={["procedureCorporateMapping", "id"]}
              label="Procedure"
              rules={[{ required: true, message: "Please select procedure" }]}
            >
              <Select
                placeholder="Select procedure"
                disabled={disabled}
                onChange={(value) => {
                  const procedure = procedureList?.find((p) => p.id === value);
                  form.setFieldsValue({
                    procedureCorporateMapping: {
                      id: value,
                      name: procedure?.procedureMethodName,
                    },
                    // Set treatment type from procedure's lineOfTreatmentId
                    treatmentType: procedure?.lineOfTreatmentId,
                    // Clear dependent fields
                    starhealthProcedureDiagnosis: null,
                    speciality: null,
                  });
                }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {procedureList?.map((procedure) => (
                  <Option key={procedure.id} value={procedure.id}>
                    {procedure.procedureMethodName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Form.Item name={["procedureCorporateMapping", "name"]} hidden>
            <Input />
          </Form.Item>

          <Col span={8}>
            <Form.Item
              name="treatmentType"
              label="Treatment Type"
              rules={[
                {
                  required: isProcedureOthers,
                  message: "Please select treatment type",
                },
              ]}
            >
              <Radio.Group disabled={!isProcedureOthers || disabled}>
                <Radio value={1}>Medical</Radio>
                <Radio value={2}>Surgical</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {isProcedureOthers ? (
            <>
              <Col span={8}>
                <Form.Item
                  name="otherProcedureText"
                  label="Procedure Name"
                  rules={[
                    { required: true, message: "Please enter procedure name" },
                  ]}
                >
                  <Input
                    placeholder="Enter procedure name"
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="otherProcedureDiagnosisText"
                  label="Provisional Diagnosis"
                  rules={[
                    { required: true, message: "Please enter diagnosis" },
                  ]}
                >
                  <Input
                    placeholder="Enter provisional diagnosis"
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["department", "id"]}
                  label="Speciality"
                  rules={[
                    { required: true, message: "Please select speciality" },
                  ]}
                >
                  <Select
                    placeholder="Select speciality"
                    disabled={disabled}
                    onChange={(value) =>
                      form.setFieldsValue({
                        department: {
                          id: value,
                          name: filteredSpecialityList?.find(
                            (s) => s.id === value
                          )?.departmentName,
                        },
                      })
                    }
                    showSearch
                    optionFilterProp="children"
                  >
                    {filteredSpecialityList?.map((spec) => (
                      <Option key={spec.id} value={spec.id}>
                        {spec.departmentName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </>
          ) : (
            <Col span={8}>
              <Form.Item
                name={["starhealthProcedureDiagnosis", "id"]}
                label="Provisional Diagnosis"
                rules={[{ required: true, message: "Please select diagnosis" }]}
              >
                <Select
                  placeholder="Select diagnosis"
                  disabled={disabled}
                  onChange={(value) =>
                    form.setFieldsValue({
                      starhealthProcedureDiagnosis: {
                        id: value,
                        name: diagnosisList?.find(
                          (d) => d.starhealthProcedureDiagnosisId === value
                        )?.illnessName,
                      },
                    })
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {diagnosisList?.map((diagnosis) => (
                    <Option
                      key={diagnosis.starhealthProcedureDiagnosisId}
                      value={diagnosis.starhealthProcedureDiagnosisId}
                    >
                      {diagnosis.illnessName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col span={8}>
            <Form.Item
              name="dateOfDiagnosis"
              label="Date of Diagnosis"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD-MM-YYYY HH:mm:ss"
                showTime
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                disabled={disabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={["chronicIllness", "id"]}
              label="Past History of Chronic Illness"
              rules={[
                { required: true, message: "Please select chronic illness" },
              ]}
            >
              <Select
                placeholder="Select chronic illness"
                disabled={disabled}
                onChange={(value) =>
                  form.setFieldsValue({
                    chronicIllness: {
                      id: value,
                      name: chronicIllnessList?.find((c) => c.id === value)
                        ?.illnessName,
                    },
                  })
                }
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {chronicIllnessList?.map((illness) => (
                  <Option key={illness.id} value={illness.id}>
                    {illness.illnessName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {isChronicIllnessOthers && (
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="chronicIllnessDate"
                label="Date of Diagnosis of Chronic Illness"
                // rules={[{ required: true, message: "Please select date" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY HH:mm:ss"
                  showTime
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="noOfMonths"
                label="Months"
                rules={[
                  { required: true, message: "Please enter number of months" },
                ]}
              >
                <Input
                  style={{ width: "100%" }}
                  min={1}
                  placeholder="Enter number of months"
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Title level={4}>Doctor Details</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="doctorName"
              label="Doctor Name"
              rules={[{ required: true, message: "Please enter doctor name" }]}
            >
              <Input placeholder="Enter doctor name" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="doctorQualification"
              label="Doctor Qualification"
              rules={[
                { required: true, message: "Please select qualification" },
              ]}
            >
              <Select
                placeholder="Select qualification"
                disabled={disabled}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {qualificationList?.map((qual) => (
                  <Option key={qual.id} value={qual.qualificationName}>
                    {qual.qualificationName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Title level={4}>Cost Details</Title>
        <Form.List name="casePreAuthStarhealthBillDetailsRequestList">
          {(fields, { add, remove }) => (
            <>
              {fields?.map((field, index) => (
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      {...field}
                      name={[field.name, "irdaiBillCategoryId"]}
                      label="Bill Category"
                      rules={[
                        { required: true, message: "Please select category" },
                      ]}
                    >
                      <Select
                        placeholder="Select category"
                        disabled={disabled}
                        onChange={(value) => {
                          onBillCategoryChange(value);
                          // Clear sub-category when category changes
                          const casePreAuthStarhealthBillDetailsRequestList =
                            form.getFieldValue(
                              "casePreAuthStarhealthBillDetailsRequestList"
                            );
                          casePreAuthStarhealthBillDetailsRequestList[
                            index
                          ].starHealthBillSubCategoryId = null;
                          casePreAuthStarhealthBillDetailsRequestList[
                            index
                          ].numberOfUnits = null;
                          casePreAuthStarhealthBillDetailsRequestList[
                            index
                          ].estimateAmount = null;
                          form.setFieldsValue({
                            casePreAuthStarhealthBillDetailsRequestList,
                          });
                        }}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {billCategories?.map((category) => (
                          <Option key={category.id} value={category.id}>
                            {category.masterCategoryName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      {...field}
                      name={[field.name, "starHealthBillSubCategoryId"]}
                      label="Bill Sub-Category"
                      rules={[
                        {
                          required: true,
                          message: "Please select sub-category",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select sub-category"
                        disabled={disabled}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        onChange={(value) => {
                          const subCategory = billSubCategories?.find(
                            (s) => s.id === value
                          );
                          const casePreAuthStarhealthBillDetailsRequestList =
                            form.getFieldValue(
                              "casePreAuthStarhealthBillDetailsRequestList"
                            );
                          casePreAuthStarhealthBillDetailsRequestList[
                            index
                          ].estimateAmount = subCategory?.perUnitAmount || null;
                          casePreAuthStarhealthBillDetailsRequestList[
                            index
                          ].isAmountPresent = !!subCategory?.perUnitAmount;
                          form.setFieldsValue({
                            casePreAuthStarhealthBillDetailsRequestList,
                          });
                        }}
                      >
                        {billSubCategories?.map((subCategory) => (
                          <Option key={subCategory.id} value={subCategory.id}>
                            {subCategory.categoryName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={5}>
                    <Form.Item
                      {...field}
                      name={[field.name, "numberOfUnits"]}
                      label="Units"
                      rules={[
                        { required: true, message: "Please enter units" },
                      ]}
                    >
                      <Input
                        style={{ width: "100%" }}
                        min={1}
                        placeholder="Enter units"
                        disabled={disabled}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={5}>
                    <Form.Item
                      {...field}
                      name={[field.name, "estimateAmount"]}
                      label="Cost"
                      rules={[
                        {
                          required: true,
                          message: "Please enter estimate Amount",
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        disabled={
                          form.getFieldValue(
                            "casePreAuthStarhealthBillDetailsRequestList"
                          )?.[index]?.isAmountPresent || disabled
                        }
                        formatter={(value) =>
                          `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        // parser={(value: string | null) =>
                        //   value
                        //     ? parseFloat(value.replace(/₹\s?|(,*)/g, ""))
                        //     : 0
                        // }
                        placeholder="Enter estimateAmount"
                      />
                    </Form.Item>
                  </Col>

                  <Col
                    span={2}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: 30,
                    }}
                  >
                    {fields.length > 1 && !disabled && (
                      <DeleteOutlined
                        onClick={() => onDeleteCategory(index)}
                        style={{ color: "#ff4d4f", fontSize: "20px" }}
                      />
                    )}
                  </Col>
                </Row>
              ))}

              {disabled ? (
                <></>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Form.Item style={{ width: "25%" }}>
                    <Button
                      type="dashed"
                      onClick={onAddCategory}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Bill Category
                    </Button>
                  </Form.Item>
                </div>
              )}
            </>
          )}
        </Form.List>

        <Row justify="end" align="middle">
          <Col>
            <Title level={5}>
              Total Amount: ₹ {totalAmount.toLocaleString()} /-
            </Title>
          </Col>
        </Row>

        {loading ? (
          <div
            style={{
              margin: "auto",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        ) : (
          documentRequestResponseDocuments?.["requestedDocuments"]?.length >
            0 &&
          (showDocuments || documentUrls?.length > 0 || !patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal) && (
            <DocumentUpload
              documentTypes={documentTypes}
              documentUrls={documentUrls}
              handleOpen={handleOpen}
              portalSubmission={hasClaimId}
              documentRequestResponseDocuments={documentRequestResponseDocuments}
              isApproved={isApproved}
              renderUploadButton={renderUploadButton}
              handleFileChange={handleFileChange}
            />
          )
        )}

        {documentRequestResponseDocuments?.["responseDocuments"]?.length > 0 &&
          (showDocuments || documentUrls?.length > 0 || !patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal) && (
            <ResponseDocumentUpload
              documentTypes={documentTypes}
              documentUrls={documentUrls}
              handleOpen={handleOpen}
              portalSubmission={hasClaimId || portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.submitted}
              documentRequestResponseDocuments={documentRequestResponseDocuments}
              handleSaveData={handleSaveData}
              currentStage={preAuth?.stepperData?.currentVisitStage}
              activeKey={activeKey}
              handleFileChange={handleFileChange}
              uploadFileList={uploadFileList}
              // renderUploadButton={renderUploadButton}
            />
          )}
      </Form>

      <div
        style={{
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "30px",
          borderTop: "1px solid #D9D9D9",
          padding: "15px 20px",
        }}
      >
        {(showDocuments || portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.approved || !patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal) ? (
          !hasClaimId && (
            <>
              {portalData?.casePreAuthStarhealth?.id &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.initiated &&
                !(
                  auth?.role?.role == "FO_STAFF" &&
                  portalData?.casePreAuthStarhealth?.casePreAuthResponse
                    ?.intimated
                ) && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                      }}
                      onClick={() => handleSaveConfirmationModal("update")}
                    >
                      UPDATE
                    </Button>
                  </div>
                )}

              {!portalData?.casePreAuthStarhealth?.id && (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "rgb(0, 76, 143)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px 0px #00000040",
                      width: "100%",
                    }}
                    onClick={() => handleSaveConfirmationModal("save")}
                  >
                    SAVE
                  </Button>
                </div>
              )}

              {hasFunctionality("casePreAuthIntimate") &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.intimated &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.initiated &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.approved && (
                  <div style={{ width: "16%" }}>
                    <Button
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                      }}
                      className="me-2"
                      // onClick={() => handleSaveIhx()}
                      onClick={() => handleSaveConfirmationModal("intimate")}
                    >
                      Send To QC
                    </Button>
                  </div>
                )}

              {hasFunctionality("preAuthApprover") &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.initiated &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.approved &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.submitted && (
                  <div style={{ width: "16%" }}>
                    <Button
                      loading={isApprovedInitiated}
                      className="me-2"
                      // onClick={handleApprovePreAuth}
                      onClick={() => handleSaveConfirmationModal("approve")}
                      disabled={isApproved}
                      // style={{ }}
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                        opacity: !isApproved ? 1 : 0.5,
                        pointerEvents: !isApproved ? "auto" : "none",
                      }}
                      // style={{display: "flex", alignItems: "center", background: "#008B8B", border: "1px solid #008B8B", boxShadow: "0px 2px 8px 0px #00000040", borderRadius: "10px", padding: "12px 25px"}}
                    >
                      QC Approve
                    </Button>
                  </div>
                )}

              {hasFunctionality("casePreAuthSubmission") &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.initiated &&
                !portalData?.casePreAuthStarhealth?.casePreAuthResponse
                  ?.submitted && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                      }}
                      onClick={handleSubmit}
                    >
                      {patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal ? "Initiate" : "Submit Manually"}
                    </Button>
                  </div>
                )}
            </>
          )
        ) : 
        !hasClaimId && (
          // <div style={{ width: "16%" }}>
          //   <Button
          //     style={{
          //       backgroundColor: "rgb(0, 76, 143)",
          //       color: "#fff",
          //       boxShadow: "0px 2px 8px 0px #00000040",
          //       width: "100%",
          //     }}
          //     className="me-2"
          //     // onClick={() => handleSaveIhx()}
          //     onClick={handleGetDocumentsStarHealth}
          //   >
          //     Fetch Documents
          //   </Button>
          // </div>
          <></>
        )}
      </div>

      {isSubmitted && showVerifyButton && handleClaimValidation ? (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={isValidationLoading}
            onClick={handleClaimValidation}
            style={{
              backgroundColor: "rgb(0, 76, 143)",
              color: "#fff",
              boxShadow: "0px 2px 8px 0px #00000040",
              minWidth: 140,
            }}
          >
            {registrationValidationStatus ? "View" : "Verify"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(PreAuthStarHealth);
